/**
 * Market context — view desk (Executive Glass).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type MarketContext } from '../../lib/api';
import { fmt } from '../../lib/format';
import { copy } from './brand';
import { MiniCandles, Sparkline } from './ui/Sparkline';
import { PullToRefresh } from './ui/PullToRefresh';
import { Skeleton } from './ui/Skeleton';
import { MobNum } from './ui/MobNum';
import { useKlines } from './hooks/useKlines';

export function MobileMarketPage() {
  const qc = useQueryClient();
  const { data: ctxBtc, isLoading } = useQuery({
    queryKey: ['context', 'BTCUSDT'],
    queryFn: () => api.get<MarketContext>('/api/data/context?symbol=BTCUSDT'),
    refetchInterval: 30_000,
  });

  const { data: ctxEth } = useQuery({
    queryKey: ['context', 'ETHUSDT'],
    queryFn: () => api.get<MarketContext>('/api/data/context?symbol=ETHUSDT'),
    refetchInterval: 60_000,
  });

  const { data: btcPrice } = useQuery({
    queryKey: ['price', 'BTCUSDT'],
    queryFn: () => api.get<{ symbol: string; price: number }>('/api/binance/price?symbol=BTCUSDT'),
    refetchInterval: 15_000,
  });

  const klines = useKlines('BTCUSDT', '1h', 48);
  const ethK = useKlines('ETHUSDT', '1h', 36);

  const fngLabel = (v: number | null | undefined) => {
    if (v == null) return '—';
    if (v <= 25) return 'Extreme Fear';
    if (v <= 45) return 'Fear';
    if (v <= 55) return 'Neutral';
    if (v <= 75) return 'Greed';
    return 'Extreme Greed';
  };

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['context'] }),
      qc.invalidateQueries({ queryKey: ['price'] }),
      qc.invalidateQueries({ queryKey: ['klines'] }),
    ]);
  };

  return (
    <div className="tc-mob-page tc-mob-page--ht tc-mob-page--eg">
      <PullToRefresh onRefresh={refresh}>
        <div className="tc-mob-page__header tc-mob-page__header--eg">
          <div className="tc-mob-page__title">{copy.market.title}</div>
          <div className="tc-mob-page__sub">{copy.market.sub}</div>
        </div>

        <div className="tc-mob-market-hero tc-mob-market-hero--ht tc-mob-market-hero--eg">
          <div className="tc-mob-market-hero__symbol">BTC / USDT</div>
          <MobNum size="xl" tone="amber" full={fmt.number(btcPrice?.price, 2)}>
            {fmt.price(btcPrice?.price)}
          </MobNum>
          <div className="tc-mob-market-hero__chart">
            {klines.isLoading ? (
              <Skeleton variant="chart" />
            ) : klines.data?.candles?.length ? (
              <MiniCandles candles={klines.data.candles.slice(-40)} height={110} />
            ) : klines.data?.closes?.length ? (
              <Sparkline values={klines.data.closes} height={90} />
            ) : (
              <div className="tc-mob-chart tc-mob-chart--empty">{copy.market.chartOffline}</div>
            )}
          </div>
        </div>

        {isLoading ? (
          <Skeleton variant="kpi" />
        ) : (
          <div className="tc-mob-kpi-scroll">
            <div className="tc-mob-kpi tc-mob-kpi--dark tc-mob-kpi--eg">
              <div className="tc-mob-kpi__label">DVOL</div>
              <MobNum size="lg" tone="cyan">
                {fmt.percent(ctxBtc?.dvol, 1)}
              </MobNum>
            </div>
            <div className="tc-mob-kpi tc-mob-kpi--dark tc-mob-kpi--eg">
              <div className="tc-mob-kpi__label">Fear & Greed</div>
              <MobNum size="lg">{ctxBtc?.fear_greed ?? '—'}</MobNum>
              <div className="tc-mob-kpi__sub">{fngLabel(ctxBtc?.fear_greed)}</div>
            </div>
            <div className="tc-mob-kpi tc-mob-kpi--dark tc-mob-kpi--eg">
              <div className="tc-mob-kpi__label">Funding</div>
              <MobNum size="md">
                {fmt.percent(
                  ctxBtc?.funding_rate != null ? Number(ctxBtc.funding_rate) * 100 : null,
                  4,
                )}
              </MobNum>
            </div>
            <div className="tc-mob-kpi tc-mob-kpi--dark tc-mob-kpi--eg">
              <div className="tc-mob-kpi__label">Open Interest</div>
              <MobNum size="md" full={fmt.number(ctxBtc?.open_interest, 0)}>
                {fmt.compact(ctxBtc?.open_interest, 2)}
              </MobNum>
            </div>
          </div>
        )}

        {ctxBtc?.max_pain != null && (
          <div className="tc-mob-section">
            <div className="tc-mob-section__title">
              <span className="tc-mob-section__pipe" /> {copy.market.maxPain}
            </div>
            <div className="tc-mob-market-detail tc-mob-card--eg" style={{ padding: '12px 14px', borderRadius: 14 }}>
              <span className="tc-mob-market-detail__label">BTC Max Pain</span>
              <MobNum size="md" full={fmt.number(ctxBtc.max_pain, 0)}>
                {fmt.compact(ctxBtc.max_pain, 2)}
              </MobNum>
            </div>
          </div>
        )}

        {ctxEth && (
          <div className="tc-mob-section">
            <div className="tc-mob-section__title">
              <span className="tc-mob-section__pipe" /> ETH / USDT
            </div>
            <div className="tc-mob-card tc-mob-card--ht tc-mob-card--eg">
              <div className="tc-mob-card__header">
                <span className="tc-mob-card__symbol">ETH</span>
                <MobNum size="md" full={fmt.number(ctxEth.spot_price, 2)}>
                  {fmt.price(ctxEth.spot_price)}
                </MobNum>
              </div>
              {ethK.data?.closes?.length ? <Sparkline values={ethK.data.closes} height={56} /> : null}
              <div className="tc-mob-card__body tc-mob-card__body--metrics">
                <div className="tc-mob-metric">
                  <div className="tc-mob-metric__label">DVOL</div>
                  <MobNum size="sm">{fmt.percent(ctxEth.dvol, 1)}</MobNum>
                </div>
                <div className="tc-mob-metric">
                  <div className="tc-mob-metric__label">Funding</div>
                  <MobNum size="sm">
                    {fmt.percent(
                      ctxEth.funding_rate != null ? Number(ctxEth.funding_rate) * 100 : null,
                      4,
                    )}
                  </MobNum>
                </div>
              </div>
            </div>
          </div>
        )}

        {ctxBtc?.macro_events && ctxBtc.macro_events.length > 0 && (
          <div className="tc-mob-section">
            <div className="tc-mob-section__title">
              <span className="tc-mob-section__pipe" /> {copy.market.macroTitle}
            </div>
            <div className="tc-mob-card-list">
              {ctxBtc.macro_events.slice(0, 5).map((ev, i) => (
                <div key={i} className="tc-mob-card tc-mob-card--ht tc-mob-card--eg tc-mob-card--compact">
                  <div className="tc-mob-card__header">
                    <span
                      className={`tc-mob-badge tc-mob-badge--${
                        ev.impact === 'high' ? 'red' : ev.impact === 'medium' ? 'amber' : 'neutral'
                      }`}
                    >
                      {ev.impact}
                    </span>
                    <span className="tc-mob-card__symbol">{ev.name}</span>
                  </div>
                  <div className="tc-mob-card__body">
                    <div className="tc-mob-card__field">
                      <span className="tc-mob-card__field-label">Tanggal</span>
                      <span className="tc-mob-card__field-value">{fmt.date(ev.date)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}
